import React, { useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Profile from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import Swal from "sweetalert2";
import { GlobalContext } from "../context/Provider";
import { toast } from "react-toastify";
import { UserNameValidation, strongPasswordValidation } from "../utils/validations";
import { Button } from "../components/ui/button";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Camera,
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  Users,
  Car,
  CalendarCheck,
  DollarSign,
  Shield,
  CheckCircle2,
} from "lucide-react";

const AddStaff: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { authState, staffMembers } = useContext(GlobalContext);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [loader, setLoader] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [newUser, setNewUser] = useState({
    name: "",
    image: "",
    email: "",
    password: "",
    language: "ENGLISH",
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return toast.error("You can upload image in JPG, PNG or WebP format");
    }

    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Image file size must be less than 5MB");
    }

    try {
      setLoader(true);
      const apiRes = await henceforthApi.Auth.uploadImage("file", file);
      setNewUser((prev) => ({
        ...prev,
        image: apiRes.data.file_name,
      }));
      toast.success("Profile photo uploaded successfully");
    } catch (err: any) {
      toast.error(err?.response?.body?.error_description || "Image upload failed");
    } finally {
      setLoader(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const formatRoleName = (role: string) => {
    return role
      .replace(/^ROLES_/i, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getRoleIcon = (role: string) => {
    const r = role.toLowerCase();
    if (r.includes("user")) return Users;
    if (r.includes("vehicle")) return Car;
    if (r.includes("booking")) return CalendarCheck;
    if (r.includes("transaction")) return DollarSign;
    return Shield;
  };

  const getRoleDescription = (role: string) => {
    const r = role.toLowerCase();
    if (r.includes("user")) return "Manage passenger & driver accounts";
    if (r.includes("vehicle")) return "Approve vehicle brands & models";
    if (r.includes("booking")) return "Monitor & track booked rides";
    if (r.includes("transaction")) return "View financial transactions & logs";
    return "Access to administrative module";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newUser.name.trim()) {
      return toast.warn("Please add staff name");
    }
    if (!UserNameValidation(newUser.name)) {
      return toast.warn("Please enter alphabets only for the name");
    }
    if (!newUser.email.trim()) {
      return toast.warn("Please add email address");
    }
    if (!newUser.password) {
      return toast.warn("Please enter a password");
    }
    if (!strongPasswordValidation(newUser.password)) {
      return toast.warn(
        "Password must contain minimum 8 characters with at least one uppercase, lowercase, digit, and special character"
      );
    }
    if (!newUser.image) {
      return toast.warn("Please upload a staff profile image");
    }
    if (!selectedRoles.length) {
      return toast.warn("Please select at least one module role");
    }

    const items = {
      ...newUser,
      name: newUser.name.trim(),
      roles: selectedRoles,
    };

    setSubmitting(true);
    henceforthApi.Administrator.addStaffMembers(items)
      .then((res: any) => {
        Swal.fire({
          title: "Staff Added",
          text: res?.data?.message || "Staff member added successfully",
          icon: "success",
          confirmButtonColor: "#006747",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/staff/1/all");
        });
      })
      .catch((err: any) => {
        Swal.fire({
          title: "Error!",
          text: err?.response?.body?.error_description || "Failed to add staff member",
          icon: "error",
          confirmButtonColor: "#006747",
          confirmButtonText: "Close",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const profileImg = newUser.image
    ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${newUser.image}`
    : Profile;

  return (
    <div className="w-full space-y-3 pb-6">
      {/* ── Top Back Button ── */}
      <div className="flex items-center justify-between pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => window.history.back()}
          className="gap-1.5 h-8 px-2.5 text-xs text-slate-700 hover:text-slate-900 border-slate-200 shadow-2xs"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Staff Management</span>
        </Button>
      </div>

      {/* ── Main Form Layout: Compact 2-Column Split ── */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* ── Left Column (lg:col-span-4): Photo & Access Summary ── */}
        <div className="lg:col-span-4 space-y-4">
          {/* Photo Card */}
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="mb-3">
              <h2 className="text-sm font-bold text-slate-800">Staff Photo</h2>
              <p className="text-xs text-slate-500">
                Staff profile picture for identification
              </p>
            </div>

            <div className="flex flex-col items-center justify-center py-3 bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
              <div className="relative group">
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-white shadow bg-white">
                  <img
                    src={profileImg}
                    alt={newUser.name || "Staff Avatar"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = Profile;
                    }}
                  />
                  {loader && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                    </div>
                  )}
                </div>

                {/* Camera Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loader}
                  title="Upload Photo"
                  className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-white text-[#006747] shadow-md flex items-center justify-center hover:bg-emerald-50 hover:scale-105 active:scale-95 transition border border-slate-200 cursor-pointer disabled:opacity-50"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>

                <input
                  ref={fileInputRef}
                  onChange={handleFile}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                />
              </div>

              <div className="mt-3 text-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loader}
                  className="text-xs h-7 px-2.5 gap-1.5"
                >
                  <Camera className="w-3 h-3" />
                  {newUser.image ? "Change Photo" : "Upload Photo"}
                </Button>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  JPG, PNG, WebP up to 5MB <span className="text-rose-500">*</span>
                </p>
              </div>
            </div>
          </div>

          {/* Access Preview Summary Card */}
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-sm font-bold text-slate-800">Assigned Access</h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-[#006747] border border-emerald-200">
                {selectedRoles.length} Active
              </span>
            </div>

            {selectedRoles.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-1">
                No module permissions selected yet. Choose permissions from the right.
              </p>
            ) : (
              <div className="space-y-1.5">
                {selectedRoles.map((role) => {
                  const Icon = getRoleIcon(role);
                  return (
                    <div
                      key={role}
                      className="flex items-center gap-2 py-1 px-2 rounded-lg bg-slate-50 border border-slate-100 text-xs font-medium text-slate-700"
                    >
                      <Icon className="w-3.5 h-3.5 text-[#006747] shrink-0" />
                      <span className="truncate">{formatRoleName(role)}</span>
                      <Check className="w-3 h-3 text-emerald-600 ml-auto shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right Column (lg:col-span-8): Unified Form Card ── */}
        <div className="lg:col-span-8 bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs space-y-5">
          {/* Section 1: Credentials */}
          <div>
            <div className="pb-3 mb-4 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-800">Account Credentials</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Staff member identification and login access
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[11px] text-slate-400 font-mono">
                    {newUser.name.length}/30
                  </span>
                </div>
                <div className="relative flex items-center">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <input
                    type="text"
                    required
                    maxLength={30}
                    name="name"
                    value={newUser.name}
                    onChange={handleChange}
                    placeholder="e.g. John Doe"
                    className="w-full pl-9 pr-3.5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-800 focus:outline-none focus:border-[#006747] focus:ring-2 focus:ring-[#006747]/20 transition"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Email Address <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <input
                    type="email"
                    required
                    maxLength={50}
                    name="email"
                    value={newUser.email}
                    onChange={handleChange}
                    placeholder="e.g. staff@readyshare.com"
                    className="w-full pl-9 pr-3.5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-800 focus:outline-none focus:border-[#006747] focus:ring-2 focus:ring-[#006747]/20 transition"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Initial Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    maxLength={30}
                    name="password"
                    value={newUser.password}
                    onChange={handleChange}
                    placeholder="Create a strong password (minimum 8 characters)"
                    className="w-full pl-9 pr-10 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-800 focus:outline-none focus:border-[#006747] focus:ring-2 focus:ring-[#006747]/20 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 text-slate-400 hover:text-slate-600 transition p-1"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-start gap-1.5 mt-1.5 text-[11px] text-slate-500">
                  <AlertCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                  <span>
                    Must contain min 8 characters with at least one uppercase, lowercase, digit, and special symbol.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Permissions */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-3 mb-3 border-b border-slate-100">
              <div>
                <h2 className="text-sm font-bold text-slate-800">
                  Module Permissions <span className="text-rose-500">*</span>
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Select which dashboard sections this staff member is authorized to manage
                </p>
              </div>
              <span className="self-start sm:self-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-[#006747] border border-emerald-200">
                {selectedRoles.length} Selected
              </span>
            </div>

            {/* Permission Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {Array.isArray(staffMembers) &&
                staffMembers.map((role: string) => {
                  const isChecked = selectedRoles.includes(role);
                  const Icon = getRoleIcon(role);
                  const desc = getRoleDescription(role);

                  return (
                    <label
                      key={role}
                      className={`relative flex items-start gap-3 p-3 rounded-xl border text-left transition cursor-pointer select-none ${isChecked
                          ? "bg-emerald-50/70 border-[#006747] ring-1 ring-[#006747]/30 shadow-2xs"
                          : "bg-white border-slate-200/90 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleRoleToggle(role)}
                        className="w-4 h-4 mt-0.5 rounded text-[#006747] border-slate-300 focus:ring-[#006747] accent-[#006747] cursor-pointer shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`w-3.5 h-3.5 ${isChecked ? "text-[#006747]" : "text-slate-500"}`} />
                          <span className={`text-xs font-bold ${isChecked ? "text-[#006747]" : "text-slate-800"}`}>
                            {formatRoleName(role)}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                          {desc}
                        </p>
                      </div>
                      {isChecked && (
                        <CheckCircle2 className="w-4 h-4 text-[#006747] shrink-0" />
                      )}
                    </label>
                  );
                })}
            </div>
          </div>

          {/* Section 3: Action Buttons directly inside Card Footer */}
          <div className="pt-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto text-xs h-9"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="w-full sm:w-auto text-xs h-9"
              disabled={submitting || loader || !newUser.name || !newUser.email || !newUser.password}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Creating Staff Account...
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Add Staff Member
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddStaff;
